/**
 * MarkdownText — render Claude's narrative output through react-markdown.
 *
 * The command engine's planner is increasingly asked to produce
 * markdown-shaped responses (headings, bullets, **bold**, numbered
 * lists). The previous renderer dropped everything into a single
 * ``<p>`` with ``whitespace-pre-line``, so the user saw the literal
 * "## Summary" and "**5 employees**" characters instead of styled
 * sections. This component routes the same text through react-markdown
 * with Tailwind ``prose`` styling, while preserving the two existing
 * features the plain renderer had:
 *
 *   1. Inline ``[N]`` citation tokens still tokenize into clickable
 *      footnote pills (``CitationRef``). Without this, switching to
 *      markdown would silently strip every citation marker.
 *   2. Empty / non-markdown text falls through unchanged so a single-
 *      line confirmation like "Created Maria Lopez." doesn't get a
 *      ``<p>`` wrapper that breaks the existing icon-flex layout.
 *
 * Design notes:
 *   • The ``prose`` plugin isn't installed — we use a handful of
 *     custom ``components`` overrides to apply Tailwind utility
 *     classes that match the rest of the bento style (heading sizes
 *     scaled down, list spacing tight, code subdued).
 *   • We intentionally do NOT enable raw HTML in markdown
 *     (``allowDangerousHtml`` is left off): the planner's output is
 *     user-influenced (it summarizes user-submitted complaints,
 *     names, etc.) and any HTML it produces would be an XSS
 *     vector against the admin viewing the response.
 *   • Code blocks render but without syntax highlighting — the
 *     command engine doesn't emit code, so investing in a
 *     highlighter would be premature.
 */
import type { ReactNode, ComponentPropsWithoutRef } from "react";
import { Children, isValidElement } from "react";
import ReactMarkdown from "react-markdown";

import {
  buildCitationMap,
  tokenizeWithCitations,
} from "./ReportRenderer";
import type { Citation } from "@/api/aiCommand";

interface MarkdownTextProps {
  /** Raw text from ``response.text`` (or a section body). */
  text: string;
  /** Citations the planner registered for this turn. ``[N]`` tokens
   *  inside ``text`` are resolved against this list and rendered as
   *  superscript footnote pills. */
  citations?: Citation[];
  /** Override the wrapper className. Defaults to a tightened-down
   *  ``prose``-equivalent that fits inside the narrative card. */
  className?: string;
}

/** Quick test for "is this even markdown-shaped?". A single line
 *  like "Created Maria Lopez." should bypass the full markdown
 *  parser and render as a flat ``<span>`` so the narrative-card
 *  icon-flex layout still works. */
function looksLikeMarkdown(text: string): boolean {
  if (!text) return false;
  // Headings, bold, italics, bullets, numbered lists, blockquotes,
  // tables, fenced code, horizontal rules — any of these triggers
  // markdown rendering. Single-line text with no special chars
  // skips it.
  return /(^|\n)\s*(#{1,6} |[*\-+] |\d+\.\s|>\s|\|.*\||```|---)/.test(text) ||
    /\*\*[^*\n]+\*\*/.test(text) ||
    /_[^_\n]+_/.test(text);
}

/**
 * Apply citation tokenization to every string node inside a React
 * subtree. Used by the markdown component overrides below — markdown
 * gives us ``<p>``/``<li>``/``<strong>`` etc., and we walk their
 * children looking for raw strings that may contain ``[N]`` tokens.
 *
 * Returns a new array of nodes with strings replaced by the
 * citation-tokenized fragments. Non-string nodes pass through
 * unchanged (we don't recurse into nested elements — citations
 * inside ``**bold**`` are rare enough that the extra complexity
 * isn't worth it).
 */
function tokenizeChildren(
  children: ReactNode,
  citations: Map<number, Citation>,
): ReactNode[] {
  if (citations.size === 0) return Children.toArray(children);
  const out: ReactNode[] = [];
  Children.forEach(children, (child) => {
    if (typeof child === "string") {
      out.push(...tokenizeWithCitations(child, citations));
    } else {
      out.push(child);
    }
  });
  return out;
}

export function MarkdownText({
  text,
  citations,
  className,
}: MarkdownTextProps) {
  const citationMap = buildCitationMap(citations);

  // Plain single-line / non-markdown text: render flat so the
  // narrative card's icon-text flex layout stays intact. This is
  // the common case for confirmation/error/short responses.
  if (!looksLikeMarkdown(text)) {
    return (
      <span className={className ?? "text-sm text-text-primary"}>
        {tokenizeWithCitations(text, citationMap)}
      </span>
    );
  }

  return (
    <div
      className={
        className ??
        // Hand-rolled prose styling — tightened down from the
        // Tailwind ``prose`` plugin defaults to fit inside the
        // narrative card. Headings get a touch of letter-spacing,
        // body text inherits the card's color so dark mode works
        // without extra config.
        "command-markdown space-y-2 text-sm text-text-primary"
      }
    >
      <ReactMarkdown
        // We intentionally do NOT pass remark-gfm/rehype-raw — keeping
        // the parser strict avoids HTML injection from user-influenced
        // planner output and keeps the bundle small.
        components={{
          h1: (props) => (
            <h1
              {...stripNode(props)}
              className="text-base font-semibold tracking-tight text-text-primary mt-3 first:mt-0"
            >
              {tokenizeChildren(props.children, citationMap)}
            </h1>
          ),
          h2: (props) => (
            <h2
              {...stripNode(props)}
              className="text-base font-semibold tracking-tight text-text-primary mt-3 first:mt-0"
            >
              {tokenizeChildren(props.children, citationMap)}
            </h2>
          ),
          h3: (props) => (
            <h3
              {...stripNode(props)}
              className="text-sm font-semibold text-text-primary mt-2 first:mt-0"
            >
              {tokenizeChildren(props.children, citationMap)}
            </h3>
          ),
          h4: (props) => (
            <h4
              {...stripNode(props)}
              className="text-sm font-semibold text-text-muted mt-2 first:mt-0"
            >
              {tokenizeChildren(props.children, citationMap)}
            </h4>
          ),
          p: (props) => (
            <p
              {...stripNode(props)}
              className="text-sm leading-relaxed text-text-primary"
            >
              {tokenizeChildren(props.children, citationMap)}
            </p>
          ),
          ul: (props) => (
            <ul
              {...stripNode(props)}
              className="list-disc list-outside pl-5 space-y-1 text-sm text-text-primary"
            >
              {props.children}
            </ul>
          ),
          ol: (props) => (
            <ol
              {...stripNode(props)}
              className="list-decimal list-outside pl-5 space-y-1 text-sm text-text-primary"
            >
              {props.children}
            </ol>
          ),
          li: (props) => (
            <li {...stripNode(props)} className="leading-relaxed">
              {tokenizeChildren(props.children, citationMap)}
            </li>
          ),
          strong: (props) => (
            <strong
              {...stripNode(props)}
              className="font-semibold text-text-primary"
            >
              {tokenizeChildren(props.children, citationMap)}
            </strong>
          ),
          em: (props) => (
            <em {...stripNode(props)} className="italic">
              {tokenizeChildren(props.children, citationMap)}
            </em>
          ),
          code: (props) => {
            // Inline ``code`` — distinguish from fenced blocks by the
            // lack of a className on the parent ``<pre>``.
            const inline = !isFenced(props);
            if (inline) {
              return (
                <code
                  {...stripNode(props)}
                  className="rounded bg-surface-muted px-1.5 py-0.5 text-[0.85em] font-mono text-text-primary"
                >
                  {props.children}
                </code>
              );
            }
            return (
              <code
                {...stripNode(props)}
                className="block whitespace-pre overflow-x-auto rounded-lg bg-surface-muted/80 p-3 text-xs font-mono text-text-primary"
              >
                {props.children}
              </code>
            );
          },
          pre: (props) => (
            <pre {...stripNode(props)} className="my-2">
              {props.children}
            </pre>
          ),
          blockquote: (props) => (
            <blockquote
              {...stripNode(props)}
              className="border-l-2 border-accent/40 pl-3 italic text-text-muted"
            >
              {tokenizeChildren(props.children, citationMap)}
            </blockquote>
          ),
          a: (props) => (
            <a
              {...stripNode(props)}
              className="text-accent underline-offset-2 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {props.children}
            </a>
          ),
          hr: () => <hr className="my-3 border-border/40" />,
          table: (props) => (
            <div className="overflow-x-auto">
              <table
                {...stripNode(props)}
                className="w-full text-xs border-collapse"
              >
                {props.children}
              </table>
            </div>
          ),
          th: (props) => (
            <th
              {...stripNode(props)}
              className="border border-border/50 bg-surface-muted px-2 py-1 text-left font-semibold"
            >
              {props.children}
            </th>
          ),
          td: (props) => (
            <td
              {...stripNode(props)}
              className="border border-border/50 px-2 py-1"
            >
              {tokenizeChildren(props.children, citationMap)}
            </td>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

/** ``react-markdown`` injects a synthetic ``node`` prop on every
 *  component override — it's the AST node and React complains about
 *  the unknown DOM attribute if we forward it. Strip it before
 *  spreading the rest of the props. */
function stripNode<T extends { node?: unknown }>(
  props: T,
): Omit<T, "node"> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { node, ...rest } = props;
  return rest;
}

/** react-markdown 9+ no longer passes an ``inline`` flag to ``code``;
 *  the convention is "code with no parent pre is inline". We check
 *  by walking the children of the parent — but markdown gives us a
 *  flat ``code`` here, so we infer from whether the value contains
 *  newlines or is wrapped in a fenced block. */
function isFenced(props: ComponentPropsWithoutRef<"code">): boolean {
  const c = props.children;
  if (typeof c === "string") return c.includes("\n");
  if (Array.isArray(c)) {
    return c.some(
      (x) => typeof x === "string" && x.includes("\n"),
    );
  }
  if (isValidElement(c)) return false;
  return false;
}
